import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bolt,
  CalendarMonth,
  ChatBubbleOutline,
  Insights,
  PeopleAlt,
  SupportAgent,
  Timeline,
} from "@mui/icons-material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import brLocale from "date-fns/locale/pt-BR";
import { format } from "date-fns";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import { i18n } from "../../translate/i18n";
import AppliedFiltersBar from "../../components/AppliedFiltersBar";
import FilterButton from "../../components/FilterButton";
import FilterPopover from "../../components/FilterPopover";
import MetricsStrip from "../../components/MetricsStrip";

const CHART_COLORS = [
  "#2563eb",
  "#14b8a6",
  "#f97316",
  "#e11d48",
  "#8b5cf6",
  "#f59e0b",
  "#0f766e",
  "#64748b",
];

const formatNumber = value =>
  new Intl.NumberFormat("pt-BR").format(Number(value || 0));

const formatPercent = value =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}%`;

const formatMinutes = value =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })} min`;

const formatDateTime = value => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch (error) {
    return value;
  }
};

const translateOrFallback = (key, fallback) => {
  const translated = i18n.t(key);
  return translated === key ? fallback : translated;
};

const dashboardText = {
  subtitle: translateOrFallback(
    "dashboard.executive.subtitle",
    "Visao consolidada da operacao, contatos e mensagens."
  ),
  applyFilters: translateOrFallback("dashboard.executive.applyFilters", "Aplicar filtros"),
  periodLabel: translateOrFallback("dashboard.executive.periodLabel", "Periodo analisado"),
  inactivityDays: translateOrFallback("dashboard.executive.inactivityDays", "Dias de inatividade"),
  overview: translateOrFallback("dashboard.executive.overview", "Visao executiva"),
  operations: translateOrFallback("dashboard.executive.operations", "Operacao"),
  contacts: translateOrFallback("dashboard.executive.contacts", "Contatos"),
  messages: translateOrFallback("dashboard.executive.messages", "Mensagens"),
  attendants: translateOrFallback("dashboard.executive.attendants", "Atendentes"),
  reactivation: translateOrFallback("dashboard.executive.reactivation", "Reativacao"),
  byQueue: translateOrFallback("dashboard.executive.byQueue", "Por fila"),
  contactStates: translateOrFallback("dashboard.executive.contactStates", "Contatos por estado"),
  contactTags: translateOrFallback("dashboard.executive.contactTags", "Contatos por tag"),
  wallets: translateOrFallback("dashboard.executive.wallets", "Carteiras e contatos publicos"),
  segmentation: translateOrFallback("dashboard.executive.segmentation", "Segmentacao da base"),
  mediaTypes: translateOrFallback("dashboard.executive.mediaTypes", "Tipos de midia"),
  outOfHours: translateOrFallback("dashboard.executive.outOfHours", "Atuacao fora do horario"),
  publicContacts: translateOrFallback("dashboard.executive.publicContacts", "Contatos publicos"),
  channels: translateOrFallback("dashboard.executive.channels", "Canais"),
  ticketsByStatus: translateOrFallback("dashboard.executive.ticketsByStatus", "Tickets por status"),
  attendantRanking: translateOrFallback(
    "dashboard.executive.attendantRanking",
    "Ranking de atendentes"
  ),
  noData: translateOrFallback("dashboard.executive.noData", "Sem dados para o periodo."),
  loadError: translateOrFallback(
    "dashboard.executive.loadError",
    "Nao foi possivel carregar os dados do dashboard."
  ),
};

const createDefaultDashboardFilters = () => ({
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  endDate: new Date(),
  inactivityDays: 30,
});

const gradientCardSx = {
  borderRadius: 4,
  border: "1px solid rgba(148, 163, 184, 0.18)",
  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
};

const filterInputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(255,255,255,0.92)",
  },
};

const getRequestErrorDetails = error => ({
  status: error?.response?.status || null,
  url: error?.config?.url || null,
  message: error?.response?.data?.error || error?.message || "Erro desconhecido",
});

const SectionHeader = ({ eyebrow, title, description, icon }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={2}
    alignItems={{ xs: "flex-start", md: "center" }}
    justifyContent="space-between"
    sx={{ mb: 3 }}
  >
    <Stack spacing={1}>
      <Chip
        size="small"
        label={eyebrow}
        sx={{
          alignSelf: "flex-start",
          bgcolor: "rgba(37, 99, 235, 0.08)",
          color: "#1d4ed8",
          fontWeight: 700,
          letterSpacing: "0.04em",
        }}
      />
      <Typography variant="h5" fontWeight={800} color="text.primary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Stack>
    <Avatar
      sx={{
        width: 52,
        height: 52,
        bgcolor: "rgba(15, 23, 42, 0.92)",
        color: "#fff",
        boxShadow: "0 10px 25px rgba(15, 23, 42, 0.2)",
      }}
    >
      {icon}
    </Avatar>
  </Stack>
);

const MetricTile = ({ label, value, helper, accent = "#2563eb" }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: "100%",
      borderRadius: 3,
      border: "1px solid rgba(148, 163, 184, 0.16)",
      background: "rgba(255,255,255,0.72)",
    }}
  >
    <Typography variant="body2" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ mt: 1, color: accent }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {helper}
    </Typography>
  </Paper>
);

const ChartCard = ({ title, subtitle, children, minHeight = 320 }) => (
  <Card sx={{ ...gradientCardSx, height: "100%" }}>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={800} color="text.primary">
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {subtitle}
        </Typography>
      ) : null}
      <Box sx={{ width: "100%", minHeight }}>{children}</Box>
    </CardContent>
  </Card>
);

const EmptyChartState = ({ message }) => (
  <Box
    sx={{
      minHeight: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 3,
      border: "1px dashed rgba(148, 163, 184, 0.4)",
      background: "rgba(248,250,252,0.75)",
      p: 3,
      textAlign: "center",
    }}
  >
    <Typography color="text.secondary">{message}</Typography>
  </Box>
);

const SimpleTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: "1px solid rgba(148, 163, 184, 0.24)",
      }}
    >
      {label ? (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      ) : null}
      {payload.map(item => (
        <Typography key={item.dataKey} variant="body2" fontWeight={700}>
          {item.name}: {formatNumber(item.value)}
        </Typography>
      ))}
    </Paper>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const themeV4 = useThemeV4();
  const { user } = useContext(AuthContext);
  const {
    getSummary,
    getTicketsStatus,
    getTicketsQueues,
    getAttendantsPerformance,
    getContactsStates,
    getContactsTags,
    getContactsWallets,
    getContactsReactivation,
    getContactsSegmentation,
    getMessagesOverview,
    getMessagesMediaTypes,
    getTicketsBotUsage,
    getTicketsOutOfHours,
  } = useDashboard();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [defaultFilters] = useState(createDefaultDashboardFilters);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [dashboardData, setDashboardData] = useState({
    summary: { counters: {}, attendants: [] },
    ticketsStatus: { total: 0, byStatus: [] },
    ticketsQueues: [],
    attendantsPerformance: [],
    contactsStates: [],
    contactsTags: [],
    contactsWallets: { publicContacts: 0, byWallet: [] },
    contactsReactivation: { total: 0, contacts: [] },
    contactsSegmentation: {
      totalContacts: 0,
      activeContacts: 0,
      inactiveContacts: 0,
      botDisabledContacts: 0,
      audioEnabledContacts: 0,
      byChannel: [],
    },
    messagesOverview: {},
    messagesMediaTypes: [],
    ticketsBotUsage: {},
    ticketsOutOfHours: { byQueue: [] },
  });

  const dashboardFallbackData = {
    summary: { counters: {}, attendants: [] },
    ticketsStatus: { total: 0, byStatus: [] },
    ticketsQueues: [],
    attendantsPerformance: [],
    contactsStates: [],
    contactsTags: [],
    contactsWallets: { publicContacts: 0, byWallet: [] },
    contactsReactivation: { total: 0, contacts: [] },
    contactsSegmentation: {
      totalContacts: 0,
      activeContacts: 0,
      inactiveContacts: 0,
      botDisabledContacts: 0,
      audioEnabledContacts: 0,
      byChannel: [],
    },
    messagesOverview: {},
    messagesMediaTypes: [],
    ticketsBotUsage: {},
    ticketsOutOfHours: { byQueue: [] },
  };

  const primaryMain = themeV4?.palette?.primary?.main || "#2563eb";

  useEffect(() => {
    loadDashboard(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildParams = currentFilters => ({
    startDate: format(currentFilters.startDate, "yyyy-MM-dd"),
    endDate: format(currentFilters.endDate, "yyyy-MM-dd"),
    inactivityDays: Number(currentFilters.inactivityDays || 30),
  });

  const loadDashboard = async currentFilters => {
    setLoading(true);

    try {
      const params = buildParams(currentFilters);
      const requests = [
        { key: "summary", endpoint: "/dashboard/summary", request: getSummary(params) },
        {
          key: "ticketsStatus",
          endpoint: "/dashboard/tickets/status",
          request: getTicketsStatus(params),
        },
        {
          key: "ticketsQueues",
          endpoint: "/dashboard/tickets/queues",
          request: getTicketsQueues(params),
        },
        {
          key: "attendantsPerformance",
          endpoint: "/dashboard/attendants/performance",
          request: getAttendantsPerformance(params),
        },
        {
          key: "contactsStates",
          endpoint: "/dashboard/contacts/states",
          request: getContactsStates(params),
        },
        {
          key: "contactsTags",
          endpoint: "/dashboard/contacts/tags",
          request: getContactsTags(params),
        },
        {
          key: "contactsWallets",
          endpoint: "/dashboard/contacts/wallets",
          request: getContactsWallets(params),
        },
        {
          key: "contactsReactivation",
          endpoint: "/dashboard/contacts/reactivation",
          request: getContactsReactivation(params),
        },
        {
          key: "contactsSegmentation",
          endpoint: "/dashboard/contacts/segmentation",
          request: getContactsSegmentation(params),
        },
        {
          key: "messagesOverview",
          endpoint: "/dashboard/messages/overview",
          request: getMessagesOverview(params),
        },
        {
          key: "messagesMediaTypes",
          endpoint: "/dashboard/messages/media-types",
          request: getMessagesMediaTypes(params),
        },
        {
          key: "ticketsBotUsage",
          endpoint: "/dashboard/tickets/bot-usage",
          request: getTicketsBotUsage(params),
        },
        {
          key: "ticketsOutOfHours",
          endpoint: "/dashboard/tickets/out-of-hours",
          request: getTicketsOutOfHours(params),
        },
      ];

      const results = await Promise.allSettled(requests.map(item => item.request));
      const nextData = { ...dashboardFallbackData };
      const failedRequests = [];

      results.forEach((result, index) => {
        const { key, endpoint } = requests[index];

        if (result.status === "fulfilled") {
          nextData[key] = result.value;
        } else {
          const errorDetails = getRequestErrorDetails(result.reason);
          failedRequests.push({
            key,
            endpoint,
            status: errorDetails.status,
            message: errorDetails.message,
            url: errorDetails.url || endpoint,
          });
          console.error(`Dashboard request failed: ${key} (${endpoint})`, {
            endpoint,
            ...errorDetails,
          });
        }
      });

      setDashboardData(nextData);

      if (failedRequests.length) {
        console.table(failedRequests);
      }

      if (failedRequests.length === requests.length) {
        toast.error(dashboardText.loadError);
      } else if (failedRequests.length > 0) {
        toast.warn(
          `Blocos com falha: ${failedRequests.map(item => item.endpoint).join(", ")}`
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(dashboardText.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    if (!draftFilters.startDate || !draftFilters.endDate) {
      toast.error(dashboardText.loadError);
      return;
    }

    if (draftFilters.startDate > draftFilters.endDate) {
      toast.error("A data inicial nao pode ser maior que a final.");
      return;
    }

    const normalizedFilters = {
      ...draftFilters,
      inactivityDays: Number(draftFilters.inactivityDays || 30),
    };

    setFilters(normalizedFilters);
    setFilterAnchorEl(null);
    loadDashboard(normalizedFilters);
  };

  const applyDashboardFilters = (nextFilters) => {
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    setFilterAnchorEl(null);
    loadDashboard(nextFilters);
  };

  const handleOpenDashboardFilters = (event) => {
    setDraftFilters(filters);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleClearDashboardFilters = () => {
    applyDashboardFilters({ ...defaultFilters });
  };

  const periodChanged =
    format(filters.startDate, "yyyy-MM-dd") !== format(defaultFilters.startDate, "yyyy-MM-dd") ||
    format(filters.endDate, "yyyy-MM-dd") !== format(defaultFilters.endDate, "yyyy-MM-dd");
  const dashboardAppliedFilters = [
    periodChanged
      ? {
          key: "period",
          label: `Periodo: ${format(filters.startDate, "dd/MM/yyyy")} - ${format(
            filters.endDate,
            "dd/MM/yyyy"
          )}`,
          onRemove: () =>
            applyDashboardFilters({
              ...filters,
              startDate: defaultFilters.startDate,
              endDate: defaultFilters.endDate,
            }),
        }
      : null,
    Number(filters.inactivityDays) !== Number(defaultFilters.inactivityDays)
      ? {
          key: "inactivity",
          label: `${dashboardText.inactivityDays}: ${filters.inactivityDays}`,
          onRemove: () =>
            applyDashboardFilters({
              ...filters,
              inactivityDays: defaultFilters.inactivityDays,
            }),
        }
      : null,
  ].filter(Boolean);

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const counters = dashboardData.summary?.counters || {};
  const attendants = dashboardData.attendantsPerformance || [];
  const statusData = dashboardData.ticketsStatus?.byStatus || [];
  const queueData = dashboardData.ticketsQueues || [];
  const statesData = dashboardData.contactsStates || [];
  const tagsData = dashboardData.contactsTags || [];
  const walletData = dashboardData.contactsWallets?.byWallet || [];
  const channelData = dashboardData.contactsSegmentation?.byChannel || [];
  const mediaTypes = dashboardData.messagesMediaTypes || [];
  const reactivationContacts = dashboardData.contactsReactivation?.contacts || [];
  const outOfHoursByQueue = dashboardData.ticketsOutOfHours?.byQueue || [];
  const onlineAttendants = attendants.filter(item => item.online).length;

  const executiveMetrics = [
    {
      key: "inAttendance",
      label: i18n.t("dashboard.cards.inAttendance"),
      value: formatNumber(counters.supportHappening),
    },
    {
      key: "waiting",
      label: i18n.t("dashboard.cards.waiting"),
      value: formatNumber(counters.supportPending),
      severity: "warning",
    },
    {
      key: "finalized",
      label: i18n.t("dashboard.cards.finalized"),
      value: formatNumber(counters.supportFinished),
      severity: "success",
    },
    {
      key: "newContacts",
      label: i18n.t("dashboard.cards.newContacts"),
      value: formatNumber(counters.leads),
    },
  ];

  const messageMetrics = [
    {
      key: "total",
      label: "Mensagens totais",
      value: formatNumber(dashboardData.messagesOverview?.totalMessages),
    },
    {
      key: "inbound",
      label: "Recebidas",
      value: formatNumber(dashboardData.messagesOverview?.inboundMessages),
    },
    {
      key: "outbound",
      label: "Enviadas",
      value: formatNumber(dashboardData.messagesOverview?.outboundMessages),
    },
    {
      key: "average",
      label: "Media por ticket",
      value: Number(dashboardData.messagesOverview?.avgMessagesPerTicket || 0).toLocaleString(
        "pt-BR",
        { maximumFractionDigits: 1 }
      ),
    },
  ];

  const operationsCards = [
    {
      label: i18n.t("dashboard.cards.averageServiceTime"),
      value: formatMinutes(counters.avgSupportTime),
      helper: "tempo medio ate concluir um atendimento",
      accent: "#1d4ed8",
    },
    {
      label: i18n.t("dashboard.cards.averageWaitingTime"),
      value: formatMinutes(counters.avgWaitTime),
      helper: "tempo medio antes do primeiro atendimento",
      accent: "#ea580c",
    },
    {
      label: i18n.t("dashboard.cards.activeTickets"),
      value: formatNumber(counters.activeTickets),
      helper: "tickets com interacao recente",
      accent: "#7c3aed",
    },
    {
      label: i18n.t("dashboard.cards.passiveTickets"),
      value: formatNumber(counters.passiveTickets),
      helper: "tickets sem atividade no periodo",
      accent: "#475569",
    },
  ];

  const tabItems = [
    { value: "overview", label: "Visao geral", icon: <Insights fontSize="small" /> },
    { value: "operations", label: "Operacao", icon: <Bolt fontSize="small" /> },
    { value: "contacts", label: "Contatos", icon: <PeopleAlt fontSize="small" /> },
    { value: "messages", label: "Mensagens", icon: <ChatBubbleOutline fontSize="small" /> },
    { value: "attendants", label: "Atendentes", icon: <SupportAgent fontSize="small" /> },
  ];

  const renderOverviewTab = () => (
    <>
      <SectionHeader
        eyebrow={dashboardText.overview}
        title="Panorama executivo"
        description="Os principais indicadores da operacao em um bloco mais limpo e orientado a decisao."
        icon={<Insights />}
      />

      <Box sx={{ mb: 4 }}>
        <MetricsStrip ariaLabel="Indicadores executivos" items={executiveMetrics} />
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                NPS e cobertura de avaliacao
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Distribuicao das avaliacoes recebidas e o indice de respostas no periodo.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <MetricTile
                    label="NPS geral"
                    value={formatPercent(counters.npsScore)}
                    helper={`${formatNumber(counters.withRating)} tickets avaliados`}
                    accent="#1d4ed8"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricTile
                    label={i18n.t("dashboard.assessments.evaluationIndex")}
                    value={formatPercent(counters.percRating)}
                    helper={`${formatNumber(counters.withoutRating)} sem avaliacao`}
                    accent="#ea580c"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricTile
                    label="Aguardando retorno"
                    value={formatNumber(counters.waitRating)}
                    helper="tickets com avaliacao pendente"
                    accent="#0f766e"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    label={i18n.t("dashboard.assessments.prosecutors")}
                    value={formatPercent(counters.npsPromotersPerc)}
                    helper="clientes que promoveriam a marca"
                    accent="#16a34a"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    label={i18n.t("dashboard.assessments.neutral")}
                    value={formatPercent(counters.npsPassivePerc)}
                    helper="clientes satisfeitos, mas pouco engajados"
                    accent="#ca8a04"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    label={i18n.t("dashboard.assessments.detractors")}
                    value={formatPercent(counters.npsDetractorsPerc)}
                    helper="clientes com maior risco de churn"
                    accent="#dc2626"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                Equipe online
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Disponibilidade da equipe no periodo filtrado.
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,233,0.12) 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center", lg: "flex-start", xl: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h3" fontWeight={900} sx={{ color: primaryMain }}>
                      {onlineAttendants}/{attendants.length}
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      atendentes online
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      minWidth: { xs: "100%", sm: 180, lg: "100%", xl: 180 },
                      borderRadius: 3,
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      background: "rgba(255,255,255,0.68)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Grupos em atendimento
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.35 }}>
                      {formatNumber(counters.supportGroups)}
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                {operationsCards.map(item => (
                  <Grid item xs={12} sm={6} lg={12} xl={6} key={item.label}>
                    <MetricTile
                      label={item.label}
                      value={item.value}
                      helper={item.helper}
                      accent={item.accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                {dashboardText.outOfHours}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {formatNumber(dashboardData.ticketsOutOfHours?.outOfHoursTickets)} tickets ficaram fora da janela operacional prevista.
              </Typography>
              {outOfHoursByQueue.length ? (
                <Stack spacing={1.5}>
                  {outOfHoursByQueue.map(item => (
                    <Box key={`${item.queue}-${item.queueId}`}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {item.queue}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(item.total)}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={
                          dashboardData.ticketsOutOfHours?.outOfHoursTickets
                            ? (item.total / dashboardData.ticketsOutOfHours.outOfHoursTickets) * 100
                            : 0
                        }
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          bgcolor: "rgba(148, 163, 184, 0.18)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            bgcolor: "#ea580c",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyChartState message={dashboardText.noData} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  const renderOperationsTab = () => (
    <>
      <SectionHeader
        eyebrow={dashboardText.operations}
        title="Operacao de tickets"
        description="Status, filas e desvios operacionais para enxergar gargalos rapidamente."
        icon={<Bolt />}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={5}>
          <ChartCard
            title={dashboardText.ticketsByStatus}
            subtitle={`Total monitorado: ${formatNumber(dashboardData.ticketsStatus?.total)}`}
          >
            {statusData.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="total"
                    nameKey="status"
                    innerRadius={74}
                    outerRadius={108}
                    paddingAngle={4}
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`${entry.status}-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<SimpleTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <ChartCard
            title={dashboardText.byQueue}
            subtitle="Volume por fila com destaque para concentracao da demanda."
          >
            {queueData.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={queueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.22)" />
                  <XAxis dataKey="queue" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <Tooltip content={<SimpleTooltip />} />
                  <Bar dataKey="total" name="Tickets" radius={[12, 12, 0, 0]}>
                    {queueData.map((entry, index) => (
                      <Cell key={`${entry.queue}-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <MetricTile
            label="Tickets com bot"
            value={formatNumber(dashboardData.ticketsBotUsage?.botTickets)}
            helper="automacao ativa no atendimento"
            accent="#2563eb"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricTile
            label="Com typebot"
            value={formatNumber(dashboardData.ticketsBotUsage?.withTypebot)}
            helper={formatPercent(dashboardData.ticketsBotUsage?.typebotUsagePercent)}
            accent="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricTile
            label="Com integracao"
            value={formatNumber(dashboardData.ticketsBotUsage?.withIntegration)}
            helper="tickets apoiados por integracoes"
            accent="#0f766e"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MetricTile
            label="Fora do horario"
            value={formatPercent(dashboardData.ticketsOutOfHours?.outOfHoursPercent)}
            helper={`${formatNumber(dashboardData.ticketsOutOfHours?.outOfHoursTickets)} tickets`}
            accent="#ea580c"
          />
        </Grid>
      </Grid>
    </>
  );

  const renderContactsTab = () => (
    <>
      <SectionHeader
        eyebrow={dashboardText.contacts}
        title="Base de contatos"
        description="Leitura de distribuicao, segmentacao e reativacao para apoiar CRM e campanhas."
        icon={<PeopleAlt />}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title={dashboardText.contactStates}
            subtitle="Estados com maior concentracao de contatos."
          >
            {statesData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statesData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.22)" />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="state"
                    width={86}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <Tooltip content={<SimpleTooltip />} />
                  <Bar dataKey="total" name="Contatos" fill="#2563eb" radius={[0, 12, 12, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title={dashboardText.contactTags}
            subtitle="Tags com maior volume para segmentacoes e acoes."
          >
            {tagsData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={tagsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.22)" />
                  <XAxis dataKey="tag" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <Tooltip content={<SimpleTooltip />} />
                  <Bar dataKey="total" name="Contatos" radius={[12, 12, 0, 0]}>
                    {tagsData.map((entry, index) => (
                      <Cell key={`${entry.tag}-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                {dashboardText.segmentation}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Indicadores rapidos da qualidade e cobertura da base.
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, rgba(15,118,110,0.08) 0%, rgba(37,99,235,0.08) 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center", lg: "flex-start", xl: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h3" fontWeight={900} sx={{ color: "#0f766e" }}>
                      {formatNumber(dashboardData.contactsSegmentation?.totalContacts)}
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      Base total
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      minWidth: { xs: "100%", sm: 190, lg: "100%", xl: 190 },
                      borderRadius: 3,
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      background: "rgba(255,255,255,0.68)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Contatos ativos
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.35, color: "#16a34a" }}>
                      {formatNumber(dashboardData.contactsSegmentation?.activeContacts)}
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid item xs={12} sm={6} lg={12} xl={6}>
                  <MetricTile
                    label="Contatos inativos"
                    value={formatNumber(dashboardData.contactsSegmentation?.inactiveContacts)}
                    helper="candidatos a reengajamento"
                    accent="#ea580c"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={12} xl={6}>
                  <MetricTile
                    label="Audio habilitado"
                    value={formatNumber(dashboardData.contactsSegmentation?.audioEnabledContacts)}
                    helper="prontos para fluxos de voz"
                    accent="#7c3aed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <MetricTile
                    label="Bot desabilitado"
                    value={formatNumber(dashboardData.contactsSegmentation?.botDisabledContacts)}
                    helper="necessitam tratamento manual"
                    accent="#dc2626"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <ChartCard
            title={dashboardText.wallets}
            subtitle={`${dashboardText.publicContacts}: ${formatNumber(
              dashboardData.contactsWallets?.publicContacts
            )}`}
          >
            {walletData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={walletData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.22)" />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={96}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <Tooltip content={<SimpleTooltip />} />
                  <Bar dataKey="total" name="Contatos" fill="#0f766e" radius={[0, 12, 12, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <ChartCard
            title={dashboardText.channels}
            subtitle="Mix de canais da base de contatos."
          >
            {channelData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={channelData}
                    dataKey="total"
                    nameKey="channel"
                    innerRadius={74}
                    outerRadius={108}
                    paddingAngle={4}
                  >
                    {channelData.map((entry, index) => (
                      <Cell key={`${entry.channel}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<SimpleTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </>
  );

  const renderMessagesTab = () => (
    <>
      <SectionHeader
        eyebrow={dashboardText.messages}
        title="Engajamento por mensagens"
        description="Visao de volume, leitura e comportamento da conversa no periodo."
        icon={<ChatBubbleOutline />}
      />

      <Box sx={{ mb: 4 }}>
        <MetricsStrip ariaLabel="Indicadores de mensagens" items={messageMetrics} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <ChartCard
            title={dashboardText.mediaTypes}
            subtitle="Predominancia dos formatos trocados com os clientes."
          >
            {mediaTypes.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={mediaTypes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.22)" />
                  <XAxis dataKey="mediaType" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                  <Tooltip content={<SimpleTooltip />} />
                  <Bar dataKey="total" name="Mensagens" radius={[12, 12, 0, 0]}>
                    {mediaTypes.map((entry, index) => (
                      <Cell key={`${entry.mediaType}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message={dashboardText.noData} />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                Indicadores de leitura
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Complemento para entender atencao e retrabalho no atendimento.
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, rgba(22,163,74,0.08) 0%, rgba(37,99,235,0.08) 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center", lg: "flex-start", xl: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h3" fontWeight={900} sx={{ color: "#16a34a" }}>
                      {formatNumber(dashboardData.messagesOverview?.readMessages)}
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      Lidas
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      minWidth: { xs: "100%", sm: 190, lg: "100%", xl: 190 },
                      borderRadius: 3,
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      background: "rgba(255,255,255,0.68)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Nao lidas
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.35, color: "#dc2626" }}>
                      {formatNumber(dashboardData.messagesOverview?.unreadMessages)}
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid item xs={12} sm={6} lg={12} xl={6}>
                  <MetricTile
                    label="Privadas"
                    value={formatNumber(dashboardData.messagesOverview?.privateMessages)}
                    helper="mensagens internas ou privadas"
                    accent="#7c3aed"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={12} xl={6}>
                  <MetricTile
                    label="Encaminhadas"
                    value={formatNumber(dashboardData.messagesOverview?.forwardedMessages)}
                    helper="conteudo repassado"
                    accent="#0f766e"
                  />
                </Grid>
                <Grid item xs={12}>
                  <MetricTile
                    label="Editadas"
                    value={formatNumber(dashboardData.messagesOverview?.editedMessages)}
                    helper="ajustes de conteudo"
                    accent="#ea580c"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  const renderAttendantsTab = () => (
    <>
      <SectionHeader
        eyebrow={dashboardText.attendants}
        title={dashboardText.attendantRanking}
        description="Quem esta puxando resultado, tempo medio e qualidade percebida pelo cliente."
        icon={<SupportAgent />}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={gradientCardSx}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Atendente</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Tickets</TableCell>
                      <TableCell align="right">Abertos</TableCell>
                      <TableCell align="right">Pendentes</TableCell>
                      <TableCell align="right">Fechados</TableCell>
                      <TableCell align="right">Espera media</TableCell>
                      <TableCell align="right">Atendimento medio</TableCell>
                      <TableCell align="right">Nota</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendants.length ? (
                      attendants.map(item => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography fontWeight={700}>{item.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.email || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.online ? "Online" : "Offline"}
                              size="small"
                              sx={{
                                bgcolor: item.online
                                  ? "rgba(22,163,74,0.12)"
                                  : "rgba(100,116,139,0.16)",
                                color: item.online ? "#15803d" : "#475569",
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(item.tickets)}</TableCell>
                          <TableCell align="right">{formatNumber(item.open)}</TableCell>
                          <TableCell align="right">{formatNumber(item.pending)}</TableCell>
                          <TableCell align="right">{formatNumber(item.closed)}</TableCell>
                          <TableCell align="right">{formatMinutes(item.avgWaitTime)}</TableCell>
                          <TableCell align="right">{formatMinutes(item.avgSupportTime)}</TableCell>
                          <TableCell align="right">
                            {item.rating
                              ? `${Number(item.rating).toLocaleString("pt-BR", {
                                  maximumFractionDigits: 1,
                                })} (${formatNumber(item.countRating)})`
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Box sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              {dashboardText.noData}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...gradientCardSx, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                {dashboardText.reactivation}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {formatNumber(dashboardData.contactsReactivation?.total)} contatos retomaram a conversa apos pelo menos {filters.inactivityDays} dias.
              </Typography>
              <Stack spacing={1.5}>
                {reactivationContacts.length ? (
                  reactivationContacts.slice(0, 5).map(contact => (
                    <Paper
                      key={contact.contactId}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(148, 163, 184, 0.16)",
                        background: "rgba(255,255,255,0.72)",
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography fontWeight={700}>{contact.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contact.number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {contact.inactiveDays} dias inativo • {formatNumber(contact.ticketsInPeriod)} tickets no periodo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ultimo ticket anterior: {formatDateTime(contact.lastTicketBeforePeriod)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <EmptyChartState message={dashboardText.noData} />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 4,
        background:
          "radial-gradient(circle at top left, rgba(37,99,235,0.12), transparent 26%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <Container maxWidth="xl">
        <FilterPopover
          id="dashboard-filters"
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          title="Filtros do dashboard"
          description="Ajuste o periodo analisado e a janela de reativacao."
          onClose={() => setFilterAnchorEl(null)}
          onClear={handleClearDashboardFilters}
          clearDisabled={dashboardAppliedFilters.length === 0}
          onApply={handleApplyFilters}
          applyLabel={loading ? "Atualizando..." : dashboardText.applyFilters}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={draftFilters.startDate}
              onChange={newValue =>
                setDraftFilters(prev => ({ ...prev, startDate: newValue }))
              }
              label={i18n.t("dashboard.date.initialDate")}
              renderInput={params => (
                <TextField {...params} fullWidth size="small" sx={filterInputSx} />
              )}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={draftFilters.endDate}
              onChange={newValue =>
                setDraftFilters(prev => ({ ...prev, endDate: newValue }))
              }
              label={i18n.t("dashboard.date.finalDate")}
              renderInput={params => (
                <TextField {...params} fullWidth size="small" sx={filterInputSx} />
              )}
            />
          </LocalizationProvider>

          <TextField
            type="number"
            size="small"
            fullWidth
            label={dashboardText.inactivityDays}
            value={draftFilters.inactivityDays}
            onChange={event =>
              setDraftFilters(prev => ({
                ...prev,
                inactivityDays: event.target.value,
              }))
            }
            inputProps={{ min: 1 }}
            sx={filterInputSx}
          />
        </FilterPopover>

        <Paper
          elevation={0}
          sx={{
            ...gradientCardSx,
            mb: 3,
            p: { xs: 1.5, md: 2 },
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,64,175,0.26) 100%)"
                : "linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(219,234,254,0.88) 100%)",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
            color: "#0f172a",
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={10}>
              <Stack spacing={0.9}>
                <Chip
                  label={dashboardText.overview}
                  size="small"
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: "rgba(37,99,235,0.08)",
                    color: "#1d4ed8",
                    fontWeight: 700,
                  }}
                />
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ lineHeight: 1.1, fontSize: { xs: "1.35rem", md: "1.6rem" } }}
                >
                  {i18n.t("dashboard.title")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", maxWidth: 720, fontSize: "0.9rem" }}
                >
                  {dashboardText.subtitle}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    icon={<CalendarMonth />}
                    label={`${format(filters.startDate, "dd/MM/yyyy")} - ${format(
                      filters.endDate,
                      "dd/MM/yyyy"
                    )}`}
                    sx={{
                      bgcolor: "rgba(37,99,235,0.08)",
                      color: "#1d4ed8",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<Timeline />}
                    label={`${dashboardText.inactivityDays}: ${filters.inactivityDays}`}
                    sx={{
                      bgcolor: "rgba(15,23,42,0.06)",
                      color: "#334155",
                      fontWeight: 600,
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                <FilterButton
                  count={dashboardAppliedFilters.length}
                  open={Boolean(filterAnchorEl)}
                  controls="dashboard-filters"
                  onClick={handleOpenDashboardFilters}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <AppliedFiltersBar
          filters={dashboardAppliedFilters}
          onClearAll={handleClearDashboardFilters}
        />

        {loading ? <LinearProgress sx={{ mb: 3, borderRadius: 999 }} /> : null}

        <Paper
          elevation={0}
          sx={{
            ...gradientCardSx,
            mb: 4,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: { xs: 1, md: 2 },
              py: 1,
              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: 999,
                backgroundColor: primaryMain,
              },
            }}
          >
            {tabItems.map(item => (
              <Tab
                key={item.value}
                value={item.value}
                icon={item.icon}
                iconPosition="start"
                label={item.label}
                sx={{
                  minHeight: 58,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: "text.primary",
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {activeTab === "overview" && renderOverviewTab()}
        {activeTab === "operations" && renderOperationsTab()}
        {activeTab === "contacts" && renderContactsTab()}
        {activeTab === "messages" && renderMessagesTab()}
        {activeTab === "attendants" && renderAttendantsTab()}
      </Container>
    </Box>
  );
};

export default Dashboard;
